export const LEAD_WEBHOOK_URL = 'https://n8n.metodopps.com.br/webhook/fluxo-boas-vindas';

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
];

// IDs de clique das plataformas de anúncio + parâmetros de rastreamento da Hotmart
const CLICK_PARAMS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'dclid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  'epik',
  'sccid',
  'igshid',
  'src',
  'sck',
  'xcod',
];

export const TRACKING_PARAMS = [...UTM_PARAMS, ...CLICK_PARAMS];

const STORAGE_KEY = 'pps_first_touch';

type FirstTouch = {
  params: Record<string, string>;
  referrer: string;
  landing_page: string;
  first_visit_at: string;
};

export type GeoData = {
  ip: string;
  city: string;
  region: string;
  country: string;
  country_code: string;
  latitude: string;
  longitude: string;
  geo_timezone: string;
};

const EMPTY_GEO: GeoData = {
  ip: '',
  city: '',
  region: '',
  country: '',
  country_code: '',
  latitude: '',
  longitude: '',
  geo_timezone: '',
};

const safeStorage = {
  get(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // modo privado / storage bloqueado — segue sem persistir
    }
  },
};

const getCookie = (name: string): string => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
};

const readCurrentParams = (): Record<string, string> => {
  const search = new URLSearchParams(window.location.search);
  return Object.fromEntries(TRACKING_PARAMS.map((p) => [p, search.get(p) || '']));
};

/**
 * Guarda a primeira entrada da sessão (UTMs, referrer e landing page) para não
 * perder a atribuição caso a pessoa navegue dentro do site antes de converter.
 */
export function captureFirstTouch(): FirstTouch {
  const stored = safeStorage.get(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as FirstTouch;
    } catch {
      // valor corrompido — sobrescreve abaixo
    }
  }
  const firstTouch: FirstTouch = {
    params: readCurrentParams(),
    referrer: document.referrer || '',
    landing_page: window.location.href,
    first_visit_at: new Date().toISOString(),
  };
  safeStorage.set(STORAGE_KEY, JSON.stringify(firstTouch));
  return firstTouch;
}

/** Parâmetros atuais da URL, completando com os da primeira entrada quando vazios. */
export function getTrackingParams(): Record<string, string> {
  const current = readCurrentParams();
  const first = captureFirstTouch().params;
  return Object.fromEntries(TRACKING_PARAMS.map((p) => [p, current[p] || first[p] || '']));
}

let geoPromise: Promise<GeoData> | null = null;

export function fetchGeo(): Promise<GeoData> {
  if (!geoPromise) {
    geoPromise = fetch('https://get.geojs.io/v1/ip/geo.json')
      .then((res) => res.json())
      .then((d) => ({
        ip: d?.ip || '',
        city: d?.city || '',
        region: d?.region || '',
        country: d?.country || '',
        country_code: d?.country_code || '',
        latitude: d?.latitude || '',
        longitude: d?.longitude || '',
        geo_timezone: d?.timezone || '',
      }))
      .catch(() => EMPTY_GEO);
  }
  return geoPromise;
}

const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
  Promise.race([promise, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);

const referrerDomain = (ref: string): string => {
  try {
    return ref ? new URL(ref).hostname : '';
  } catch {
    return '';
  }
};

export type LeadInput = {
  name: string;
  email: string;
  phone: string;
  product_id: string;
  product_name: string;
  product_price: string;
  checkout_url: string;
  /** Texto livre da pessoa (dúvida enviada pelo chat). */
  message?: string;
  /** De onde veio o lead: 'checkout' (formulário de compra) ou 'chat_whatsapp'. */
  lead_source?: string;
};

/** Máscara de telefone BR: (51) 99999-9999 */
export const formatPhone = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

/** Monta o payload completo — todo campo sempre presente, vazio quando não houver valor. */
export async function buildLeadPayload(lead: LeadInput): Promise<Record<string, string>> {
  const firstTouch = captureFirstTouch();
  const params = getTrackingParams();
  const geo = await withTimeout(fetchGeo(), 1500, EMPTY_GEO);

  const fbclid = params.fbclid;
  const fbc = getCookie('_fbc') || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : '');

  const payload: Record<string, string> = {
    ...lead,
    message: lead.message ?? '',
    lead_source: lead.lead_source ?? 'checkout',
    ...params,
    fbp: getCookie('_fbp'),
    fbc,
    referrer: document.referrer || '',
    referrer_domain: referrerDomain(document.referrer),
    first_referrer: firstTouch.referrer,
    first_referrer_domain: referrerDomain(firstTouch.referrer),
    landing_page: firstTouch.landing_page,
    first_visit_at: firstTouch.first_visit_at,
    page_url: window.location.href,
    page_path: window.location.pathname,
    query_string: window.location.search.replace(/^\?/, ''),
    ...geo,
    user_agent: navigator.userAgent || '',
    language: navigator.language || '',
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    screen: `${window.screen?.width || ''}x${window.screen?.height || ''}`,
    submitted_at: new Date().toISOString(),
  };

  // Garante string em tudo (nunca undefined/null)
  return Object.fromEntries(Object.entries(payload).map(([k, v]) => [k, v == null ? '' : String(v)]));
}

/**
 * Envia como form-urlencoded em modo no-cors: é uma "simple request" (sem preflight),
 * então chega no n8n mesmo sem CORS liberado, e o keepalive mantém o envio vivo
 * durante o redirecionamento para o checkout.
 */
export function sendLead(payload: Record<string, string>): Promise<void> {
  const body = new URLSearchParams(payload);
  return fetch(LEAD_WEBHOOK_URL, {
    method: 'POST',
    mode: 'no-cors',
    keepalive: true,
    body,
  })
    .then(() => undefined)
    .catch(() => {
      // Último recurso: sendBeacon também envia urlencoded sem preflight
      try {
        navigator.sendBeacon?.(LEAD_WEBHOOK_URL, body);
      } catch {
        // nada mais a fazer
      }
    });
}
